import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    AlertTriangle,
    Loader2,
    RefreshCw,
    ShieldAlert,
    Ban,
    CheckCircle2,
    Clock,
    XCircle,
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';

/**
 * Conferência de acordos — camada 3.
 *
 * Três divergências que hoje ninguém enxerga, cada uma com sua causa:
 *
 * 1. **Boleto vivo em acordo.** A fatura virou 'Parcelado'/'Quitado' mas o
 *    boleto continua registrado e pagável no Sicoob. Marcar o status é rótulo
 *    NOSSO; o título é registro do BANCO. Se o cliente pagar, paga dívida já
 *    renegociada — e ainda continua sendo cobrado pela parcela do mês.
 *
 * 2. **Pagamento após acordo.** `fn_protect_parcelado_status` protege
 *    'Parcelado'/'Quitado' de virarem Cancelado/Vencido/Em aberto, mas nunca
 *    cobriu 'Pago'. A baixa passa direto e a fatura-mãe aparece quitada — com
 *    cara de boa notícia, que é o que torna o evento caro.
 *
 * 3. **Fila de cancelamento.** O que o worker tentou baixar e não conseguiu.
 *
 * ⚠️ Enfileirar aqui NÃO cancela nada sozinho: o worker só chama o Sicoob quando
 * `system_settings.boleto_cancelamento_automatico = 'true'`. É deliberado —
 * Sicoob §10.3 exige confirmação antes de baixar boleto e §10.6 proíbe lote sem
 * aprovação da lista completa.
 */

type Aba = 'boleto_vivo' | 'pagamento_apos' | 'fila';

const LIMITE_LISTA = 500;

const COLUNAS_FATURA =
    'id, uc_codigo, cliente_nome, mes_referencia, data_vencimento, valor_boleto_vigor, ' +
    'status_boleto_vigor, num_cobranca_boleto, conta_sicoob, data_limite_pagamento_boleto, ' +
    'cancelamento_sicoob_erro, pagamento_apos_acordo_origem, pagamento_apos_acordo_em, data_pgto_boleto';

interface FaturaDivergente {
    id: number;
    uc_codigo: string | null;
    cliente_nome: string | null;
    mes_referencia: string | null;
    data_vencimento: string | null;
    valor_boleto_vigor: number | null;
    status_boleto_vigor: string | null;
    num_cobranca_boleto: string | null;
    conta_sicoob: string | null;
    data_limite_pagamento_boleto: string | null;
    cancelamento_sicoob_erro: string | null;
    pagamento_apos_acordo_origem?: string | null;
    pagamento_apos_acordo_em?: string | null;
    data_pgto_boleto?: string | null;
}

interface ItemFila {
    id: string;
    fatura_id: number;
    num_cobranca_boleto: string;
    conta_sicoob: string | null;
    motivo: string;
    origem: string;
    status: string;
    tentativas: number;
    error_details: string | null;
    created_at: string;
    processado_em: string | null;
}

const brl = (v: number | null | undefined) =>
    (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataBr = (v: string | null | undefined) => {
    if (!v) return '—';
    // Data pura (YYYY-MM-DD) não pode passar por new Date(): o parse vira UTC e
    // volta um dia atrás no fuso do Brasil.
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const [a, m, d] = v.split('-');
        return `${d}/${m}/${a}`;
    }
    return new Date(v).toLocaleString('pt-BR');
};

const STATUS_FILA_INFO: Record<string, { label: string; className: string; Icon: React.ElementType }> = {
    pendente: { label: 'Pendente', className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400', Icon: Clock },
    processando: { label: 'Processando', className: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400', Icon: Loader2 },
    concluido: { label: 'Concluído', className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400', Icon: CheckCircle2 },
    erro: { label: 'Erro', className: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400', Icon: XCircle },
    ignorado: { label: 'Ignorado', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300', Icon: Ban },
};

const CabecalhoAba: React.FC<{
    ativa: boolean;
    onClick: () => void;
    rotulo: string;
    contagem: number;
    alerta?: boolean;
}> = ({ ativa, onClick, rotulo, contagem, alerta }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
            ativa
                ? 'bg-primary text-white shadow-lg shadow-primary/20'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-primary/40'
        }`}
    >
        {rotulo}
        <span
            className={`px-2 py-0.5 rounded-lg text-[11px] font-black ${
                ativa
                    ? 'bg-white/20'
                    : alerta && contagem > 0
                    ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}
        >
            {contagem}
        </span>
    </button>
);

const CelulaCliente: React.FC<{ fatura: FaturaDivergente }> = ({ fatura }) => (
    <td className="px-3 py-3">
        <p className="font-bold text-gray-800 dark:text-gray-200 truncate max-w-[280px]">
            {fatura.cliente_nome || '—'}
        </p>
        <p className="text-[11px] text-gray-400">
            UC {(fatura.uc_codigo || '').trim()} · id {fatura.id}
        </p>
    </td>
);

const ConferenciaAcordosView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const [aba, setAba] = useState<Aba>('boleto_vivo');
    const [loading, setLoading] = useState(true);
    const [enfileirando, setEnfileirando] = useState(false);

    const [boletoVivo, setBoletoVivo] = useState<FaturaDivergente[]>([]);
    const [totalBoletoVivo, setTotalBoletoVivo] = useState(0);
    const [pagamentoApos, setPagamentoApos] = useState<FaturaDivergente[]>([]);
    const [fila, setFila] = useState<ItemFila[]>([]);
    const [armado, setArmado] = useState(false);
    const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());

    const podeEnfileirar = profile?.role === 'adm' || profile?.role === 'gerente';

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [vivosRes, pagosRes, filaRes, settingRes] = await Promise.all([
                // ⚠️ count separado do range: o PostgREST corta em 1000 linhas por
                // padrão e o total já mentiu em Analytics/Kanban por causa disso.
                supabase
                    .from('faturas')
                    .select(COLUNAS_FATURA, { count: 'exact' })
                    .in('status_boleto_vigor', ['Parcelado', 'Quitado'])
                    .is('cancelado_em', null)
                    .is('data_pgto_boleto', null)
                    .not('num_cobranca_boleto', 'is', null)
                    .neq('status_fatura', 'Excluído')
                    .order('data_vencimento', { ascending: true })
                    .range(0, LIMITE_LISTA - 1),
                supabase
                    .from('faturas')
                    .select(COLUNAS_FATURA)
                    .eq('pagamento_apos_acordo', true)
                    .order('pagamento_apos_acordo_em', { ascending: false })
                    .range(0, LIMITE_LISTA - 1),
                supabase
                    .from('boleto_cancelamento_fila')
                    .select('*')
                    .order('created_at', { ascending: false })
                    .range(0, LIMITE_LISTA - 1),
                supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'boleto_cancelamento_automatico')
                    .maybeSingle(),
            ]);

            if (vivosRes.error) throw vivosRes.error;
            if (pagosRes.error) throw pagosRes.error;
            if (filaRes.error) throw filaRes.error;

            setBoletoVivo((vivosRes.data as unknown as FaturaDivergente[]) || []);
            setTotalBoletoVivo(vivosRes.count ?? (vivosRes.data?.length || 0));
            setPagamentoApos((pagosRes.data as unknown as FaturaDivergente[]) || []);
            setFila((filaRes.data as unknown as ItemFila[]) || []);
            setArmado(String(settingRes.data?.value ?? '').toLowerCase() === 'true');
        } catch (err: any) {
            console.error(err);
            addToast('Erro ao carregar conferência: ' + (err?.message || ''), 'error');
        } finally {
            setLoading(false);
        }
    }, [addToast]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    // Já em voo na fila — não faz sentido oferecer para enfileirar de novo.
    const faturasEmVoo = useMemo(() => {
        const s = new Set<number>();
        for (const i of fila) {
            if (i.status === 'pendente' || i.status === 'processando') s.add(i.fatura_id);
        }
        return s;
    }, [fila]);

    const exposicao = useMemo(
        () => boletoVivo.reduce((acc, f) => acc + Number(f.valor_boleto_vigor || 0), 0),
        [boletoVivo],
    );

    const elegiveis = useMemo(
        () => boletoVivo.filter(f => !faturasEmVoo.has(f.id)),
        [boletoVivo, faturasEmVoo],
    );

    const enfileirarSelecionadas = async () => {
        const alvos = elegiveis.filter(f => selecionadas.has(f.id));
        if (alvos.length === 0) return;

        setEnfileirando(true);
        try {
            const { error } = await supabase.from('boleto_cancelamento_fila').insert(
                alvos.map(f => ({
                    fatura_id: f.id,
                    num_cobranca_boleto: String(f.num_cobranca_boleto),
                    conta_sicoob: f.conta_sicoob,
                    motivo: `Passivo histórico — fatura ${f.status_boleto_vigor} com boleto vivo`,
                    origem: 'manual',
                })),
            );
            if (error) throw error;
            logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'parcelamentos', acao: 'enfileirar_cancelamento_boleto', origem: 'acao' });

            addToast(
                armado
                    ? `${alvos.length} boleto(s) enfileirado(s). O worker vai baixar no Sicoob na próxima rodada.`
                    : `${alvos.length} boleto(s) enfileirado(s). Nada será cancelado enquanto o cancelamento automático estiver desligado.`,
                'success',
            );
            setSelecionadas(new Set());
            await carregar();
        } catch (err: any) {
            addToast('Erro ao enfileirar: ' + (err?.message || ''), 'error');
        } finally {
            setEnfileirando(false);
        }
    };

    const alternar = (id: number) => {
        setSelecionadas(prev => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id);
            else s.add(id);
            return s;
        });
    };

    const selecionarTodas = () => {
        setSelecionadas(prev => (prev.size === elegiveis.length ? new Set() : new Set(elegiveis.map(f => f.id))));
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <Loader2 className="animate-spin text-primary mb-4" size={40} />
                <p className="font-bold text-sm animate-pulse">Conferindo acordos...</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Estado do cancelamento automático */}
            <div
                className={`rounded-2xl border p-4 flex items-start gap-3 ${
                    armado
                        ? 'bg-emerald-50/60 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-900/40'
                        : 'bg-amber-50/60 border-amber-200 dark:bg-amber-900/10 dark:border-amber-900/40'
                }`}
            >
                <ShieldAlert size={20} className={`mt-0.5 shrink-0 ${armado ? 'text-emerald-600' : 'text-amber-600'}`} />
                <div className="text-sm">
                    <p className="font-black text-gray-800 dark:text-gray-200">
                        Cancelamento automático: {armado ? 'LIGADO' : 'DESLIGADO'}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 mt-0.5">
                        {armado
                            ? 'Toda fatura marcada como Parcelado/Quitado tem o boleto baixado no Sicoob automaticamente.'
                            : 'Os itens ficam na fila mas nenhum boleto é baixado. Ligue em system_settings.boleto_cancelamento_automatico quando quiser armar.'}
                    </p>
                </div>
            </div>

            {/* Abas */}
            <div className="flex items-center gap-2 flex-wrap">
                <CabecalhoAba ativa={aba === 'boleto_vivo'} onClick={() => setAba('boleto_vivo')} rotulo="Boleto vivo em acordo" contagem={totalBoletoVivo} alerta />
                <CabecalhoAba ativa={aba === 'pagamento_apos'} onClick={() => setAba('pagamento_apos')} rotulo="Pagamento após acordo" contagem={pagamentoApos.length} alerta />
                <CabecalhoAba ativa={aba === 'fila'} onClick={() => setAba('fila')} rotulo="Fila de cancelamento" contagem={fila.length} />
                <button
                    onClick={carregar}
                    className="ml-auto px-3 py-2.5 rounded-xl text-sm font-bold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary/40 transition-all flex items-center gap-2"
                >
                    <RefreshCw size={15} /> Atualizar
                </button>
            </div>

            {aba === 'boleto_vivo' ? (
                <div className="space-y-4">
                    <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-900/10 p-4 flex items-start gap-3">
                        <AlertTriangle size={20} className="text-red-600 mt-0.5 shrink-0" />
                        <div className="text-sm">
                            <p className="font-black text-gray-800 dark:text-gray-200">
                                {totalBoletoVivo} fatura(s) · {brl(exposicao)} de boleto cobrável
                            </p>
                            <p className="text-gray-600 dark:text-gray-400 mt-0.5">
                                Faturas dentro de um acordo cujo boleto nunca foi baixado no Sicoob. Se o cliente
                                pagar, paga dívida já renegociada — e a parcela do mês continua sendo cobrada pela
                                régua.
                                {totalBoletoVivo > boletoVivo.length ? (
                                    <span className="font-bold"> Exibindo as {boletoVivo.length} mais antigas.</span>
                                ) : null}
                            </p>
                        </div>
                    </div>

                    {podeEnfileirar && boletoVivo.length > 0 ? (
                        <div className="flex items-center gap-3 flex-wrap">
                            <button
                                onClick={selecionarTodas}
                                className="px-3 py-2 rounded-xl text-xs font-bold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                            >
                                Selecionar / limpar todas
                            </button>
                            <button
                                onClick={enfileirarSelecionadas}
                                disabled={selecionadas.size === 0 || enfileirando}
                                className="px-4 py-2 rounded-xl text-xs font-bold bg-primary text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {enfileirando ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                                Enfileirar cancelamento ({selecionadas.size})
                            </button>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Revise a lista antes de enfileirar — baixa de boleto afeta o cliente final.
                            </span>
                        </div>
                    ) : null}

                    <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                                    {podeEnfileirar ? <th className="px-3 py-3 w-10" /> : null}
                                    <th className="px-3 py-3">Cliente / UC</th>
                                    <th className="px-3 py-3">Mês</th>
                                    <th className="px-3 py-3">Vencimento</th>
                                    <th className="px-3 py-3">Valor</th>
                                    <th className="px-3 py-3">Status</th>
                                    <th className="px-3 py-3">Boleto</th>
                                    <th className="px-3 py-3">Limite pgto.</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {boletoVivo.map(f => {
                                    const emVoo = faturasEmVoo.has(f.id);
                                    return (
                                        <tr key={f.id} className="hover:bg-gray-50/60 dark:hover:bg-gray-800/30">
                                            {podeEnfileirar ? (
                                                <td className="px-3 py-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selecionadas.has(f.id)}
                                                        onChange={() => alternar(f.id)}
                                                        disabled={emVoo}
                                                        className="accent-primary w-4 h-4 disabled:opacity-30"
                                                    />
                                                </td>
                                            ) : null}
                                            <CelulaCliente fatura={f} />
                                            <td className="px-3 py-3 font-medium">{f.mes_referencia || '—'}</td>
                                            <td className="px-3 py-3">{dataBr(f.data_vencimento)}</td>
                                            <td className="px-3 py-3 font-bold">{brl(f.valor_boleto_vigor)}</td>
                                            <td className="px-3 py-3">
                                                <span className="px-2 py-0.5 rounded-lg text-[11px] font-bold bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
                                                    {f.status_boleto_vigor}
                                                </span>
                                                {emVoo ? (
                                                    <span className="ml-1 px-2 py-0.5 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                                                        na fila
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-3">
                                                <span className="font-mono text-xs">{f.num_cobranca_boleto}</span>
                                                <p className="text-[11px] text-gray-400">{f.conta_sicoob || '—'}</p>
                                            </td>
                                            <td className="px-3 py-3">
                                                {f.data_limite_pagamento_boleto ? (
                                                    <span className="text-red-600 dark:text-red-400 font-bold">
                                                        {dataBr(f.data_limite_pagamento_boleto)}
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="text-gray-400"
                                                        title="Sem limite na nossa base — confira no console do Sicoob se ainda aceita pagamento"
                                                    >
                                                        não registrado
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            {aba === 'pagamento_apos' ? (
                <div className="space-y-4">
                    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm text-gray-600 dark:text-gray-400">
                        Faturas que saíram de <strong>Parcelado/Quitado</strong> direto para <strong>Pago</strong>. O
                        dinheiro entrou de verdade — o risco é a dívida ser contada duas vezes (uma na fatura-mãe,
                        outra nas parcelas) e o cliente seguir sendo cobrado por uma parcela que ele acha que já
                        pagou. Confira cada caso antes de dar por certo.
                    </div>

                    {pagamentoApos.length === 0 ? (
                        <div className="text-center py-16 text-sm text-gray-400 font-medium">
                            Nenhum pagamento em fatura sob acordo detectado desde que o carimbo entrou.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                                        <th className="px-3 py-3">Cliente / UC</th>
                                        <th className="px-3 py-3">Mês</th>
                                        <th className="px-3 py-3">Valor</th>
                                        <th className="px-3 py-3">Era</th>
                                        <th className="px-3 py-3">Detectado em</th>
                                        <th className="px-3 py-3">Pago em</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                    {pagamentoApos.map(f => (
                                        <tr key={f.id} className="hover:bg-gray-50/60 dark:hover:bg-gray-800/30">
                                            <CelulaCliente fatura={f} />
                                            <td className="px-3 py-3 font-medium">{f.mes_referencia || '—'}</td>
                                            <td className="px-3 py-3 font-bold">{brl(f.valor_boleto_vigor)}</td>
                                            <td className="px-3 py-3">
                                                <span className="px-2 py-0.5 rounded-lg text-[11px] font-bold bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
                                                    {f.pagamento_apos_acordo_origem || '—'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3">{dataBr(f.pagamento_apos_acordo_em)}</td>
                                            <td className="px-3 py-3">{dataBr(f.data_pgto_boleto)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : null}

            {aba === 'fila' ? (
                <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800">
                    {fila.length === 0 ? (
                        <div className="text-center py-16 text-sm text-gray-400 font-medium">Fila vazia.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                                    <th className="px-3 py-3">Fatura</th>
                                    <th className="px-3 py-3">Boleto</th>
                                    <th className="px-3 py-3">Origem</th>
                                    <th className="px-3 py-3">Status</th>
                                    <th className="px-3 py-3">Tent.</th>
                                    <th className="px-3 py-3">Criado</th>
                                    <th className="px-3 py-3">Detalhe</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {fila.map(item => {
                                    const info = STATUS_FILA_INFO[item.status] || STATUS_FILA_INFO.pendente;
                                    return (
                                        <tr key={item.id} className="hover:bg-gray-50/60 dark:hover:bg-gray-800/30">
                                            <td className="px-3 py-3 font-mono text-xs">{item.fatura_id}</td>
                                            <td className="px-3 py-3">
                                                <span className="font-mono text-xs">{item.num_cobranca_boleto}</span>
                                                <p className="text-[11px] text-gray-400">{item.conta_sicoob || '—'}</p>
                                            </td>
                                            <td className="px-3 py-3 text-xs font-medium">{item.origem}</td>
                                            <td className="px-3 py-3">
                                                <span
                                                    className={`px-2 py-0.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1 ${info.className}`}
                                                >
                                                    <info.Icon size={11} /> {info.label}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 font-bold">{item.tentativas}</td>
                                            <td className="px-3 py-3 text-xs">{dataBr(item.created_at)}</td>
                                            <td
                                                className="px-3 py-3 text-xs text-gray-500 max-w-[320px] truncate"
                                                title={item.error_details || ''}
                                            >
                                                {item.error_details || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default ConferenciaAcordosView;
